import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';

const OAUTH_URL = 'https://api.revvly.ai/mcp';

const API_KEY_CONFIG = `{
  "mcpServers": {
    "revvly": {
      "type": "http",
      "url": "https://api.revvly.ai/mcp",
      "headers": {
        "Authorization": "Bearer rvly_sk_live_<your-key>"
      }
    }
  }
}`;

const INSPECTOR = `npx @modelcontextprotocol/inspector
# Transport: Streamable HTTP
# URL:     https://api.revvly.ai/mcp
# Header:  Authorization: Bearer rvly_sk_live_<your-key>
# Or leave the header empty and complete the sign-in flow.`;

const TOOLS: { name: string; detail: string }[] = [
  ['search_docs', 'How Revvly works — pricing models, billing, catalog, analytics.'],
  ['query_clients', 'Customer organizations: name, region, trial, dashboard link.'],
  ['query_subscriptions', 'Subscriptions for a client or across the workspace.'],
  [
    'get_billing_schedule',
    'Why one subscription bills when it does — a prepaid span, an awaited go-live.',
  ],
  ['query_invoices', 'Invoices by client, status, or date. Credit notes are flagged.'],
  ['get_analytics', 'Live MRR, ARR, churn, and NRR.'],
  ['query_email_engagement', 'Sent emails with open counts and open rate (opens only).'],
  ['query_usage', 'Live meter counters, usage audit, and the next-bill projection.'],
  ['list_open_usage_readings', 'Closed-period readings missing an in-arrears quantity.'],
  ['get_receivables', 'AR summary, past-due accounts, and per-client outstanding.'],
  ['query_lifecycle_events', 'Subscription lifecycle log with MRR movement and when it happened.'],
  ['list_regions', 'Regions and currencies you sell in.'],
  [
    'list_catalog',
    'Plans, capability groups, usage components and metered units — one or several per call.',
  ],
  ['get_catalog_prices', 'Base prices for a bundle or module.'],
  ['create_client', 'Propose a new client. Nothing is written until a human approves in Revvly.'],
  ['update_client', 'Propose an edit to an existing client — only the fields you set are changed.'],
  ['create_subscription', 'Propose a new subscription.'],
  [
    'query_discounts',
    "The discount library, and a subscription's components and applied discounts.",
  ],
  ['apply_discount', 'Propose a discount on a subscription.'],
  ['get_component_prices', 'Included units, tier or step boundaries and band prices of a plan.'],
  ['list_forms', 'Forms with status, submission counts and hosted link.'],
  ['get_form', 'One form: questions, settings, follow-up steps and unpublished changes.'],
  ['query_form_submissions', 'Submissions to one form with every answer and step outcome.'],
  ['summarize_form_submissions', 'Counts per day and source, follow-up health, winning options.'],
  ['export_form_submissions', 'A CSV preview of submissions with a dashboard link to the file.'],
  ['create_bundle', 'Propose a catalog bundle (draft).'],
  ['create_module', 'Propose a module (draft).'],
  ['create_metric', 'Propose a Meter.'],
  ['create_variable_component', 'Propose a usage component around a Meter (draft).'],
  ['set_catalog_prices', 'Propose base prices for a bundle or module.'],
  ['set_available_periods', 'Propose the billing periods a draft bundle or module offers.'],
  ['attach_variable_component', 'Propose attaching a published usage component to a draft.'],
  ['publish_catalog_entity', 'Propose publishing a bundle, module or usage component.'],
  ['enter_usage_reading', 'Propose sealing a missing in-arrears usage quantity.'],
  ['waive_usage_reading', 'Propose waiving a closed period so it bills nothing.'],
  ['create_form', 'Propose a new form as an unpublished draft.'],
  ['update_form', 'Propose edits to a form draft.'],
  ['publish_form', 'Propose publishing a form draft.'],
  ['unpublish_form', 'Propose taking a published form offline.'],
  ['archive_form', 'Propose archiving a form.'],
  ['retry_form_submission', 'Propose re-running the failed follow-up steps of a submission.'],
].map(([name, detail]) => ({ name, detail }));

export function McpConnection() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Connect Claude with MCP — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <header className="dp-header">
        <span className="rv-eyebrow-mini">API Reference</span>
        <h1>
          Connect Claude to your <em>billing</em> data.
        </h1>
        <p className="dp-oneliner">
          Revvly speaks the Model Context Protocol. Claude &mdash; or any MCP client &mdash; can ask
          the same live questions as the in-dashboard Assistant: clients, subscriptions, invoices,
          MRR, and catalog. Write-scoped connections can propose changes, but only a signed-in human
          can approve them in Revvly. Tenant comes from the credential, never from the question.
        </p>
      </header>

      <section className="dp-section">
        <h2>What MCP is here</h2>
        <p className="dp-section-sub">
          The <Link to="/docs/assistant">Assistant</Link> already answers billing questions inside
          Revvly. MCP is the same tool catalog on a second transport, so those answers work from
          Claude Desktop, Claude Code, claude.ai, MCP Inspector, or your own agent stack. There is
          no second authorization system and no GraphQL hop &mdash; the tools call the same
          tenant-scoped services the dashboard does.
        </p>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            Writes use the same human-review boundary as the Assistant. A mutating tool stores a
            proposal and returns an approval link; nothing is written until the credential owner
            approves it while signed in to Revvly. Approval is not an MCP tool. Request{' '}
            <span className="api-inline">mcp:write</span> on OAuth, or issue an API key with coarse{' '}
            <span className="api-inline">WRITE</span>.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <h2>Endpoint</h2>
        <div className="api-endpoint-bar">
          <span className="api-method">POST</span>
          <span className="api-url">/mcp</span>
          <span className="api-returns">MCP · Streamable HTTP</span>
        </div>
        <p className="dp-section-sub">
          Full URL: <span className="api-inline">{OAUTH_URL}</span>. The transport is Streamable
          HTTP and stateless. GET and DELETE are accepted for protocol probes. Authenticate with
          either an OAuth access token (interactive connect) or an API key (machine clients). A
          dashboard sign-in token is not accepted here.
        </p>
      </section>

      <section className="dp-section">
        <h2>Connect with OAuth</h2>
        <p className="dp-section-sub">
          You do not paste a key. Sign in with your dashboard account and pick the workspace the
          client may read. How you add the connector differs between claude.ai and Claude Desktop.
        </p>
        <h3 className="api-sub-heading">claude.ai</h3>
        <p className="dp-section-sub">
          Add Revvly as a custom connector and set the URL to{' '}
          <span className="api-inline">{OAUTH_URL}</span>. Leave headers empty. On Team and
          Enterprise plans only organization Owners can add custom connectors; members may only
          connect to connectors previously added by an Owner.
        </p>
        <h3 className="api-sub-heading">Claude Desktop</h3>
        <p className="dp-section-sub">
          Add a remote MCP server and set the URL to <span className="api-inline">{OAUTH_URL}</span>
          . Leave headers empty.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">1. Remote URL</div>
            <div className="dp-field-v">
              Use the product-specific step above. The URL is the same:{' '}
              <span className="api-inline">{OAUTH_URL}</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">2. Sign in</div>
            <div className="dp-field-v">
              Claude opens Revvly. Sign in with your usual dashboard account. The first request is
              unauthenticated; Revvly answers with a discovery header so Claude can register itself
              and complete the authorization.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">3. Pick a workspace</div>
            <div className="dp-field-v">
              The consent page lists the workspaces you belong to. Choose one. Every connection
              receives <span className="api-inline">mcp:read</span>. If the OAuth client requested{' '}
              <span className="api-inline">mcp:write</span>, the token can also propose changes;
              approval still happens only in the signed-in Revvly dashboard. Confirm, and Claude
              receives an access token bound to that workspace.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">4. Ask</div>
            <div className="dp-field-v">
              Claude can now ask about that workspace: overdue invoices, current MRR, who is
              renewing, what plans you sell. The workspace is never a tool argument.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>Connect with an API key</h2>
        <p className="dp-section-sub">
          Claude Code, MCP Inspector, and other machine clients send a Bearer API key. Issue one
          from <strong>Settings &rarr; API Keys</strong>. The key needs the coarse{' '}
          <span className="api-inline">READ</span> capability. Add{' '}
          <span className="api-inline">WRITE</span> to propose actions. The key cannot approve its
          proposals; its credential owner opens the returned approval link while signed in to the
          Revvly dashboard. An empty endpoint scope, or a scope that includes{' '}
          <span className="api-inline">/mcp</span> or <span className="api-inline">*</span>, is
          enough. A key scoped only to <span className="api-inline">/usage</span> is rejected.
        </p>
        <span className="api-code-label">claude_desktop_config.json / MCP server entry</span>
        <pre className="api-code">{API_KEY_CONFIG}</pre>
        <p className="dp-section-sub">
          Capture the plaintext when the key is issued &mdash; it is shown once. The tenant is the
          key&rsquo;s tenant. Rate limits are the same per-key limits as the rest of the platform.
        </p>
      </section>

      <section className="dp-section">
        <h2>What you can ask</h2>
        <p className="dp-section-sub">
          The same read tools as the <Link to="/docs/assistant">Assistant</Link>. Typical questions:
          current MRR and how it moved, overdue invoices for a client, usage against a meter, what
          bundles you sell. Mutating tools return a proposal with an approval link. The credential
          owner opens that link and approves or declines while signed in to Revvly; the MCP client
          cannot approve its own request. OAuth clients must request{' '}
          <span className="api-inline">mcp:write</span>; API keys need coarse{' '}
          <span className="api-inline">WRITE</span>. There are 20 read tools and 19 proposal tools;
          the full list with access levels is in the{' '}
          <Link to="/docs/reference/mcp-tools">MCP tool catalogue</Link>.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          {TOOLS.map((row) => (
            <div className="dp-field-row" key={row.name}>
              <div className="dp-field-k">
                <span className="api-inline">{row.name}</span>
              </div>
              <div className="dp-field-v">{row.detail}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="dp-section">
        <h2>Try it locally</h2>
        <p className="dp-section-sub">
          MCP Inspector is useful when you are standing up a key or checking that OAuth reaches your
          login page.
        </p>
        <pre className="api-code">{INSPECTOR}</pre>
      </section>

      <section className="dp-section">
        <h2>Errors &amp; limits</h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">401</span>
            </div>
            <div className="dp-field-v">
              Missing or invalid credential. The response includes a{' '}
              <span className="api-inline">WWW-Authenticate</span> header so OAuth clients can
              discover how to sign in.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">403</span>
            </div>
            <div className="dp-field-v">
              An OAuth token without <span className="api-inline">mcp:read</span>, an API key
              without <span className="api-inline">READ</span>, or a write tool called without{' '}
              <span className="api-inline">mcp:write</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">429</span>
            </div>
            <div className="dp-field-v">
              Rate limit exceeded. See <span className="api-inline">Retry-After</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Tool error</div>
            <div className="dp-field-v">
              A failed tool returns a plain message on the MCP response. Stack traces are never sent
              to the client. Every call is recorded (tool name and argument names, not values).
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>Take actions</h2>
        <p className="dp-section-sub">
          Writes use the same human-review boundary as the in-dashboard Assistant. A mutating tool
          stores a 15-minute proposal and returns an <span className="api-inline">actionId</span>{' '}
          plus an approval link. The credential owner must approve in Revvly; no approval operation
          is model-callable. The server atomically claims the action id so retries cannot run the
          write twice. Request <span className="api-inline">mcp:write</span> on OAuth (it is never
          added for you), or issue an API key with coarse <span className="api-inline">WRITE</span>.
        </p>
        <Figure
          title="A write through MCP"
          caption="The client can only propose; the credential owner approves in Revvly, and the write runs once."
        >
          <Flow
            steps={[
              {
                title: 'Client calls a tool',
                text: 'A mutating tool, with mcp:write or a WRITE key.',
              },
              {
                title: 'Proposal stored',
                text: 'Valid for 15 minutes. Returns an actionId and an approval link.',
              },
              {
                title: 'Owner opens the link',
                text: 'Signed in to Revvly. The client cannot approve.',
                tone: 'warn',
              },
              {
                fork: [
                  {
                    title: 'Approved',
                    text: 'The write is applied once, even if retried.',
                    tone: 'accent',
                  },
                  { title: 'Declined', text: 'Nothing is written.', tone: 'muted' },
                ],
              },
            ]}
          />
        </Figure>
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/assistant" style={{ color: 'inherit', textDecoration: 'none' }}>
              The Revvly Assistant ›
            </Link>
          </li>
          <li>
            <Link to="/docs/concepts/apiKey" style={{ color: 'inherit', textDecoration: 'none' }}>
              API Key ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/api/graphql-public"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Public GraphQL API ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
